import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import AGRIWARE_LOGO_B64 from '../assets/logoB64';

// ─── Shared helpers ───────────────────────────────────────────────────────────

const hexToRgb = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return [r, g, b];
};

const formatDate = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatDateTime = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatTime = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

// ─── Shared header/footer ─────────────────────────────────────────────────────

const addHeader = (doc, org, docType, docRef, brandColour) => {
  const [r, g, b] = hexToRgb(brandColour || '#1A3A5C');
  const W = doc.internal.pageSize.getWidth();

  // Brand colour band
  doc.setFillColor(r, g, b);
  doc.rect(0, 0, W, 22, 'F');

  // Org name left
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.text(org?.name || 'Organisation', 14, 9);

  // Org subtitle
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(255, 255, 255, 0.6);
  doc.setTextColor(200, 215, 230);
  doc.text(org?.subtitle || '', 14, 14);

  // Agriware logo centre
  try {
    doc.addImage(AGRIWARE_LOGO_B64, 'PNG', W / 2 - 10, 2, 20, 18, undefined, 'FAST');
  } catch (e) {
    // logo failed silently
  }

  // Doc type right
  doc.setTextColor(180, 200, 220);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.text(docType, W - 14, 8, { align: 'right' });

  // Doc ref right
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(11);
  doc.text(docRef, W - 14, 15, { align: 'right' });

  doc.setTextColor(0, 0, 0);
};

const addFooter = (doc, org, pageNum, totalPages) => {
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();

  doc.setDrawColor(220, 220, 220);
  doc.line(14, H - 10, W - 14, H - 10);

  doc.setFontSize(7);
  doc.setTextColor(180, 180, 180);
  doc.setFont('helvetica', 'normal');
  doc.text(`${org?.name || ''} · Veritas · veritas.agriware.uk · An Agriware product`, 14, H - 6);
  doc.text(`Page ${pageNum} of ${totalPages}`, W - 14, H - 6, { align: 'right' });

  doc.setTextColor(0, 0, 0);
};

const addStatusBar = (doc, label, detail, type = 'info') => {
  const W = doc.internal.pageSize.getWidth();
  const colours = {
    danger: { bg: [253, 240, 240], border: [220, 53, 69], text: [220, 53, 69] },
    warning: { bg: [255, 249, 240], border: [253, 126, 20], text: [253, 126, 20] },
    success: { bg: [240, 253, 244], border: [40, 167, 69], text: [40, 167, 69] },
    info: { bg: [240, 244, 255], border: [26, 58, 92], text: [26, 58, 92] },
  };
  const c = colours[type] || colours.info;

  doc.setFillColor(...c.bg);
  doc.rect(0, 22, W, 8, 'F');
  doc.setDrawColor(...c.border);
  doc.setLineWidth(0.5);
  doc.line(0, 30, W, 30);

  // Dot
  doc.setFillColor(...c.text);
  doc.circle(17, 26.5, 1.5, 'F');

  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...c.text);
  doc.text(label, 21, 27.5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(120, 120, 120);
  doc.text(detail, W - 14, 27.5, { align: 'right' });

  doc.setLineWidth(0.1);
  doc.setTextColor(0, 0, 0);
};

const addMetaRow = (doc, cells, yStart = 30) => {
  const W = doc.internal.pageSize.getWidth();
  const cellW = (W - 28) / cells.length;

  cells.forEach((cell, i) => {
    const x = 14 + i * cellW;
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(150, 150, 150);
    doc.text(cell.label.toUpperCase(), x, yStart + 6);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(26, 26, 26);
    doc.text(String(cell.value || '—'), x, yStart + 12);

    if (i < cells.length - 1) {
      doc.setDrawColor(230, 230, 230);
      doc.line(x + cellW, yStart + 2, x + cellW, yStart + 16);
    }
  });

  doc.setDrawColor(230, 230, 230);
  doc.line(0, yStart + 18, W, yStart + 18);

  return yStart + 20;
};

const sectionTitle = (doc, label, y, colour = [150, 150, 150]) => {
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...colour);
  doc.text(label.toUpperCase(), 14, y);
  doc.setDrawColor(...colour);
  doc.setLineWidth(0.3);
  const W = doc.internal.pageSize.getWidth();
  doc.line(14, y + 1.5, W - 14, y + 1.5);
  doc.setLineWidth(0.1);
  doc.setTextColor(0, 0, 0);
  return y + 6;
};

// ─── 1. Check Record PDF ──────────────────────────────────────────────────────

export const generateCheckRecordPDF = (check, org) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const brandColour = org?.brandColour || '#1A3A5C';
  const [br, bg, bb] = hexToRgb(brandColour);
  const W = doc.internal.pageSize.getWidth();

  const result = check.result || 'pass';
  const statusType = result === 'pass' ? 'success' : result === 'fail' ? 'danger' : 'warning';
  const statusLabel = result === 'pass' ? 'PASSED' : result === 'fail' ? `FAILED — ${check.failedItems?.length || 0} items` : 'ISSUES NOTED';
  const statusDetail = result === 'fail' ? 'Asset blocked automatically · ' + formatDateTime(check.submittedAt) : formatDateTime(check.submittedAt);

  addHeader(doc, { ...org, subtitle: `${check.branchName || ''} Branch` }, 'CHECK RECORD', `CHK-${check.id?.slice(-6).toUpperCase() || '000000'}`, brandColour);
  addStatusBar(doc, statusLabel, statusDetail, statusType);

  let y = addMetaRow(doc, [
    { label: 'Completed by', value: check.submittedByName },
    { label: 'Date & time', value: formatDateTime(check.submittedAt) },
    { label: 'Template', value: check.templateName },
    { label: 'Branch', value: check.branchName },
  ]);

  // Asset
  y = sectionTitle(doc, 'Asset', y);
  const assetCells = [
    { label: 'Make & model', value: check.assetName },
    { label: 'Registration', value: check.assetReg || '—' },
    { label: 'Type', value: check.assetType || '—' },
    { label: 'Mileage', value: check.mileage ? `${Number(check.mileage).toLocaleString()} mi` : '—' },
  ];
  assetCells.forEach((cell, i) => {
    const x = 14 + i * ((W - 28) / 4);
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(150, 150, 150);
    doc.text(cell.label, x, y);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(26, 26, 26);
    doc.text(String(cell.value || '—'), x, y + 5);
  });
  y += 12;

  // Check items
  y = sectionTitle(doc, 'Check items', y);
  const sections = check.sections || [];
  sections.forEach(section => {
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(br, bg, bb);
    doc.text(section.title?.toUpperCase() || '', 14, y);
    y += 4;

    (section.items || []).forEach(item => {
      const passed = item.result !== 'fail';
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(80, 80, 80);
      doc.text(item.label || item.name || '', 17, y);

      // Result dot
      doc.setFillColor(passed ? 40 : 220, passed ? 167 : 53, passed ? 69 : 69);
      doc.circle(W - 25, y - 1.5, 1.5, 'F');

      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(passed ? 40 : 220, passed ? 167 : 53, passed ? 69 : 69);
      doc.text(passed ? 'PASS' : 'FAIL', W - 20, y, { align: 'right' });

      doc.setDrawColor(245, 245, 245);
      doc.line(14, y + 1, W - 14, y + 1);
      y += 5;

      if (y > 260) {
        addFooter(doc, org, doc.internal.getNumberOfPages(), '?');
        doc.addPage();
        y = 14;
      }
    });
    y += 2;
  });

  // Failed items
  if (result === 'fail' && check.failedItems?.length > 0) {
    y = sectionTitle(doc, 'Failed items', y, [220, 53, 69]);
    check.failedItems.forEach(item => {
      doc.setFillColor(253, 245, 245);
      doc.rect(14, y - 3, W - 28, 12, 'F');
      doc.setFillColor(220, 53, 69);
      doc.rect(14, y - 3, 1.5, 12, 'F');

      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(220, 53, 69);
      doc.text(item.label || item.name || '', 18, y + 1);

      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(item.note || item.driverNote || '', 18, y + 6);
      y += 15;
    });
  }

  // Audit trail
  y = sectionTitle(doc, 'Audit trail', y);
  doc.setFillColor(250, 250, 250);
  doc.rect(14, y - 2, W - 28, 20, 'F');
  const auditLines = [
    `${formatTime(check.submittedAt)}  Check submitted by ${check.submittedByName || '—'} via Veritas app`,
    result === 'fail' ? `${formatTime(check.submittedAt)}  Asset blocked automatically following failed check` : null,
    `${formatTime(check.submittedAt)}  Push notification sent to branch manager`,
  ].filter(Boolean);
  auditLines.forEach((line, i) => {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text(line, 18, y + 2 + i * 5);
  });
  y += auditLines.length * 5 + 6;

  // Signatures
  y = sectionTitle(doc, 'Signatures', y);
  const halfW = (W - 28) / 2;
  doc.setDrawColor(200, 200, 200);
  doc.setLineDash([1, 1]);
  doc.rect(14, y, halfW - 4, 14);
  doc.rect(14 + halfW + 4, y, halfW - 4, 14);
  doc.setLineDash([]);
  doc.setFontSize(7);
  doc.setTextColor(150, 150, 150);
  doc.text(`Operator — ${check.submittedByName || '—'}`, 16, y - 1);
  doc.text('Manager acknowledgement', 18 + halfW, y - 1);
  doc.text(`Signed digitally · ${formatTime(check.submittedAt)}`, 16, y + 8);
  doc.text('Pending', 18 + halfW, y + 8);

  addFooter(doc, org, 1, 1);
  doc.save(`check-record-${check.id?.slice(-6) || 'export'}.pdf`);
};

// ─── 2. Incident Report PDF ───────────────────────────────────────────────────

export const generateIncidentReportPDF = (incident, org) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const brandColour = org?.brandColour || '#1A3A5C';
  const [br, bg, bb] = hexToRgb(brandColour);
  const W = doc.internal.pageSize.getWidth();

  const severityType = incident.severity === 'high' ? 'danger' : incident.severity === 'medium' ? 'warning' : 'info';
  const severityLabel = `${(incident.severity || 'UNKNOWN').toUpperCase()} SEVERITY · ${(incident.status || 'Open').toUpperCase()}`;

  addHeader(doc, { ...org, subtitle: `${incident.branchId || ''} Branch` }, 'INCIDENT REPORT', `INC-${incident.id?.slice(-6).toUpperCase() || '000000'}`, brandColour);
  addStatusBar(doc, severityLabel, `Detected ${formatDateTime(incident.detectedAt)} · ${incident.location || ''}`, severityType);

  let y = addMetaRow(doc, [
    { label: 'Driver', value: incident.driverName },
    { label: 'Vehicle', value: incident.assetReg },
    { label: 'G-force', value: incident.gforce ? `${incident.gforce}G` : '—' },
    { label: 'Status', value: incident.status || 'Open' },
  ]);

  // Timeline
  y = sectionTitle(doc, 'Incident timeline', y, [br, bg, bb]);
  const events = incident.timeline || [
    { time: incident.detectedAt, text: `Crash detection triggered — ${incident.gforce || '?'}G impact detected`, type: 'danger' },
    { time: incident.detectedAt, text: 'Push notification sent to branch manager', type: 'info' },
    { time: incident.updatedAt, text: 'Incident logged and investigation opened', type: 'info' },
  ];
  events.forEach((event, i) => {
    const isRed = event.type === 'danger';
    doc.setFillColor(isRed ? 220 : br, isRed ? 53 : bg, isRed ? 69 : bb);
    doc.circle(17, y + 1, 1.5, 'F');
    if (i < events.length - 1) {
      doc.setDrawColor(200, 200, 200);
      doc.line(17, y + 3, 17, y + 8);
    }
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(br, bg, bb);
    doc.text(formatTime(event.time), 21, y + 1.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.setFontSize(8.5);
    doc.text(event.text, 40, y + 1.5);
    y += 8;
  });
  y += 4;

  // Pre-incident check
  y = sectionTitle(doc, 'Pre-incident check record', y);
  const checkCells = [
    { label: 'Last check', value: formatDateTime(incident.lastCheckAt) },
    { label: 'Check result', value: incident.lastCheckResult?.toUpperCase() || '—' },
    { label: 'Completed by', value: incident.lastCheckBy || '—' },
    { label: 'Reference', value: incident.lastCheckId ? `CHK-${incident.lastCheckId.slice(-6).toUpperCase()}` : '—' },
  ];
  checkCells.forEach((cell, i) => {
    const x = 14 + i * ((W - 28) / 4);
    doc.setFontSize(6.5);
    doc.setTextColor(150, 150, 150);
    doc.text(cell.label, x, y);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    const isFailResult = cell.label === 'Check result' && cell.value === 'FAILED';
    doc.setTextColor(isFailResult ? 220 : 26, isFailResult ? 53 : 26, isFailResult ? 69 : 26);
    doc.text(String(cell.value), x, y + 5);
    doc.setTextColor(0, 0, 0);
  });
  y += 14;

  // Driver details
  y = sectionTitle(doc, 'Driver details', y);
  const driverCells = [
    { label: 'Name', value: incident.driverName },
    { label: 'Licence number', value: incident.driverLicence || '—' },
    { label: 'Categories', value: incident.licenceCategories?.join(', ') || '—' },
    { label: 'Endorsements', value: incident.endorsements != null ? String(incident.endorsements) : '—' },
  ];
  driverCells.forEach((cell, i) => {
    const x = 14 + i * ((W - 28) / 4);
    doc.setFontSize(6.5);
    doc.setTextColor(150, 150, 150);
    doc.text(cell.label, x, y);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(26, 26, 26);
    doc.text(String(cell.value || '—'), x, y + 5);
  });
  y += 14;

  // Actions & notes
  y = sectionTitle(doc, 'Actions & notes', y);
  doc.setFillColor(250, 250, 250);
  doc.rect(14, y - 2, W - 28, 20, 'F');
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 80, 80);
  const notes = doc.splitTextToSize(incident.notes || 'No notes recorded.', W - 32);
  doc.text(notes, 18, y + 3);

  addFooter(doc, org, 1, 1);
  doc.save(`incident-report-${incident.id?.slice(-6) || 'export'}.pdf`);
};

// ─── 3. Monthly Branch Compliance PDF ────────────────────────────────────────

export const generateMonthlyCompliancePDF = (checks, assets, incidents, branch, org, monthLabel) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const brandColour = org?.brandColour || '#1A3A5C';
  const [br, bg, bb] = hexToRgb(brandColour);
  const W = doc.internal.pageSize.getWidth();

  const passed = checks.filter(c => c.result === 'pass').length;
  const failed = checks.filter(c => c.result === 'fail').length;
  const issues = checks.filter(c => c.result === 'issues').length;
  const complianceScore = checks.length > 0 ? Math.round((passed / checks.length) * 100) : 0;
  const scoreType = complianceScore >= 90 ? 'success' : complianceScore >= 75 ? 'warning' : 'danger';
  const blockedAssets = assets.filter(a => a.isBlocked);

  addHeader(doc, { ...org, subtitle: `${branch?.name || ''} Branch · ${monthLabel}` }, 'COMPLIANCE REPORT', `Monthly · ${monthLabel}`, brandColour);
  addStatusBar(doc, `COMPLIANCE SCORE · ${complianceScore}%`, `Generated ${formatDate(new Date())} · ${branch?.name || ''} Branch`, scoreType);

  let y = 32;

  // Stat boxes
  const stats = [
    { label: 'Total checks', value: checks.length, colour: [br, bg, bb] },
    { label: 'Passed', value: passed, colour: [40, 167, 69] },
    { label: 'Failed', value: failed, colour: [220, 53, 69] },
    { label: 'Issues noted', value: issues, colour: [253, 126, 20] },
  ];
  const boxW = (W - 28 - 9) / 4;
  stats.forEach((stat, i) => {
    const x = 14 + i * (boxW + 3);
    doc.setFillColor(250, 250, 250);
    doc.setDrawColor(235, 235, 235);
    doc.roundedRect(x, y, boxW, 16, 2, 2, 'FD');
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...stat.colour);
    doc.text(String(stat.value), x + 4, y + 10);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(130, 130, 130);
    doc.text(stat.label, x + 4, y + 14);
  });
  y += 22;

  // Check completion table
  y = sectionTitle(doc, 'Check completion by result', y, [br, bg, bb]);
  autoTable(doc, {
    startY: y,
    head: [['Staff member', 'Asset', 'Date & time', 'Template', 'Result']],
    body: checks.slice(0, 30).map(c => [
      c.submittedByName || '—',
      `${c.assetName || '—'}${c.assetReg ? ` (${c.assetReg})` : ''}`,
      formatDateTime(c.submittedAt),
      c.templateName || '—',
      c.result?.toUpperCase() || '—',
    ]),
    styles: { fontSize: 7.5, cellPadding: 2 },
    headStyles: { fillColor: [br, bg, bb], textColor: 255, fontStyle: 'bold', fontSize: 7 },
    columnStyles: { 4: { fontStyle: 'bold' } },
    didDrawCell: (data) => {
      if (data.section === 'body' && data.column.index === 4) {
        const val = data.cell.raw;
        if (val === 'FAIL') doc.setTextColor(220, 53, 69);
        else if (val === 'PASS') doc.setTextColor(40, 167, 69);
        else doc.setTextColor(253, 126, 20);
      }
    },
    margin: { left: 14, right: 14 },
  });
  y = doc.lastAutoTable.finalY + 8;

  // Blocked assets
  y = sectionTitle(doc, 'Blocked assets this period', y, [220, 53, 69]);
  if (blockedAssets.length === 0) {
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text('No assets blocked this period.', 14, y + 4);
    y += 10;
  } else {
    autoTable(doc, {
      startY: y,
      head: [['Asset', 'Registration', 'Reason', 'Blocked', 'Status']],
      body: blockedAssets.map(a => [
        `${a.make || ''} ${a.model || ''}`.trim(),
        a.reg || '—',
        a.blockedReason || '—',
        formatDate(a.blockedAt),
        a.isBlocked ? 'Active' : 'Unblocked',
      ]),
      styles: { fontSize: 7.5, cellPadding: 2 },
      headStyles: { fillColor: [220, 53, 69], textColor: 255, fontStyle: 'bold', fontSize: 7 },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // Incidents
  y = sectionTitle(doc, 'Incidents this period', y);
  if (incidents.length === 0) {
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text('No incidents recorded this period.', 14, y + 4);
  } else {
    autoTable(doc, {
      startY: y,
      head: [['Driver', 'Vehicle', 'Date', 'Severity', 'Status']],
      body: incidents.map(i => [
        i.driverName || '—',
        i.assetReg || '—',
        formatDate(i.detectedAt),
        (i.severity || '—').toUpperCase(),
        (i.status || '—').toUpperCase(),
      ]),
      styles: { fontSize: 7.5, cellPadding: 2 },
      headStyles: { fillColor: [br, bg, bb], textColor: 255, fontStyle: 'bold', fontSize: 7 },
      margin: { left: 14, right: 14 },
    });
  }

  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    addFooter(doc, org, i, pageCount);
  }
  doc.save(`compliance-report-${branch?.id || 'branch'}-${monthLabel.replace(' ', '-')}.pdf`);
};

// ─── 4. Group Compliance PDF ──────────────────────────────────────────────────

export const generateGroupCompliancePDF = (branches, org, monthLabel) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });
  const brandColour = org?.brandColour || '#1A3A5C';
  const [br, bg, bb] = hexToRgb(brandColour);
  const W = doc.internal.pageSize.getWidth();

  const totalChecks = branches.reduce((s, b) => s + (b.checksTotal || 0), 0);
  const totalBlocked = branches.reduce((s, b) => s + (b.blocked || 0), 0);
  const totalIncidents = branches.reduce((s, b) => s + (b.incidents || 0), 0);
  const avgScore = branches.length > 0 ? Math.round(branches.reduce((s, b) => s + (b.complianceScore || 0), 0) / branches.length) : 0;
  const scoreType = avgScore >= 90 ? 'success' : avgScore >= 75 ? 'warning' : 'danger';

  addHeader(doc, { ...org, subtitle: `All branches · ${monthLabel}` }, 'GROUP COMPLIANCE REPORT', `Monthly · ${monthLabel}`, brandColour);
  addStatusBar(doc, `GROUP SCORE · ${avgScore}% · ${branches.length} branches`, `Generated ${formatDate(new Date())}`, scoreType);

  let y = 32;

  // Stat boxes
  const stats = [
    { label: 'Branches', value: branches.length, colour: [br, bg, bb] },
    { label: 'Total checks', value: totalChecks, colour: [br, bg, bb] },
    { label: 'Assets blocked', value: totalBlocked, colour: totalBlocked > 0 ? [220, 53, 69] : [40, 167, 69] },
    { label: 'Open incidents', value: totalIncidents, colour: totalIncidents > 0 ? [220, 53, 69] : [40, 167, 69] },
    { label: 'Group score', value: `${avgScore}%`, colour: avgScore >= 90 ? [40, 167, 69] : avgScore >= 75 ? [253, 126, 20] : [220, 53, 69] },
  ];
  const boxW = (W - 28 - 12) / 5;
  stats.forEach((stat, i) => {
    const x = 14 + i * (boxW + 3);
    doc.setFillColor(250, 250, 250);
    doc.setDrawColor(235, 235, 235);
    doc.roundedRect(x, y, boxW, 16, 2, 2, 'FD');
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...stat.colour);
    doc.text(String(stat.value), x + 4, y + 10);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(130, 130, 130);
    doc.text(stat.label, x + 4, y + 14);
  });
  y += 22;

  // Branch comparison table
  y = sectionTitle(doc, 'Branch comparison', y, [br, bg, bb]);
  autoTable(doc, {
    startY: y,
    head: [['Branch', 'Total checks', 'Passed', 'Failed', 'Blocked assets', 'Open incidents', 'Compliance score']],
    body: branches.map(b => [
      b.name || b.id,
      b.checksTotal || 0,
      b.checksPassed || 0,
      b.checksFailed || 0,
      b.blocked || 0,
      b.incidents || 0,
      `${b.complianceScore || 0}%`,
    ]),
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: [br, bg, bb], textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
    columnStyles: {
      3: { textColor: [220, 53, 69], fontStyle: 'bold' },
      4: { textColor: [220, 53, 69], fontStyle: 'bold' },
      6: { fontStyle: 'bold' },
    },
    margin: { left: 14, right: 14 },
  });

  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    addFooter(doc, org, i, pageCount);
  }
  doc.save(`group-compliance-${monthLabel.replace(' ', '-')}.pdf`);
};

// ─── 5. Asset Compliance History PDF ─────────────────────────────────────────

export const generateAssetHistoryPDF = (asset, checks, incidents, org) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const brandColour = org?.brandColour || '#1A3A5C';
  const [br, bg, bb] = hexToRgb(brandColour);
  const W = doc.internal.pageSize.getWidth();

  const statusType = asset.isBlocked ? 'danger' : 'success';
  const statusLabel = asset.isBlocked ? 'CURRENTLY BLOCKED' : 'AVAILABLE';
  const statusDetail = `${asset.make || ''} ${asset.model || ''} · ${asset.mileage ? asset.mileage.toLocaleString() + ' mi' : '—'}`;

  addHeader(doc, { ...org, subtitle: asset.branchId || '' }, 'ASSET HISTORY', asset.reg || asset.serialNumber || 'ASSET', brandColour);
  addStatusBar(doc, statusLabel, statusDetail, statusType);

  let y = addMetaRow(doc, [
    { label: 'Make & model', value: `${asset.make || ''} ${asset.model || ''}`.trim() },
    { label: 'MOT expiry', value: asset.mot || '—' },
    { label: 'Tax expiry', value: asset.tax || '—' },
    { label: 'Assigned to', value: asset.assignedTo || 'Unassigned' },
  ]);

  // Check history
  y = sectionTitle(doc, 'Check history', y, [br, bg, bb]);
  autoTable(doc, {
    startY: y,
    head: [['Date & time', 'Completed by', 'Template', 'Result', 'Mileage']],
    body: checks.map(c => [
      formatDateTime(c.submittedAt),
      c.submittedByName || '—',
      c.templateName || '—',
      (c.result || '—').toUpperCase(),
      c.mileage ? `${Number(c.mileage).toLocaleString()} mi` : '—',
    ]),
    styles: { fontSize: 7.5, cellPadding: 2 },
    headStyles: { fillColor: [br, bg, bb], textColor: 255, fontStyle: 'bold', fontSize: 7 },
    didDrawCell: (data) => {
      if (data.section === 'body' && data.column.index === 3) {
        const val = data.cell.raw;
        if (val === 'FAIL') doc.setTextColor(220, 53, 69);
        else if (val === 'PASS') doc.setTextColor(40, 167, 69);
        else doc.setTextColor(253, 126, 20);
      }
    },
    margin: { left: 14, right: 14 },
  });
  y = doc.lastAutoTable.finalY + 8;

  // Block history
  y = sectionTitle(doc, 'Block history', y, [220, 53, 69]);
  const blockHistory = asset.blockHistory || (asset.isBlocked ? [{
    blockedAt: asset.blockedAt,
    reason: asset.blockedReason,
    unblockedAt: null,
    unblockedBy: null,
  }] : []);

  if (blockHistory.length === 0) {
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text('No blocks recorded.', 14, y + 4);
    y += 10;
  } else {
    autoTable(doc, {
      startY: y,
      head: [['Blocked', 'Reason', 'Unblocked', 'By', 'Duration']],
      body: blockHistory.map(b => [
        formatDateTime(b.blockedAt),
        b.reason || '—',
        b.unblockedAt ? formatDateTime(b.unblockedAt) : 'Pending',
        b.unblockedBy || '—',
        b.unblockedAt ? 'Resolved' : 'Active',
      ]),
      styles: { fontSize: 7.5, cellPadding: 2 },
      headStyles: { fillColor: [220, 53, 69], textColor: 255, fontStyle: 'bold', fontSize: 7 },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // Incidents
  y = sectionTitle(doc, 'Incidents', y);
  if (incidents.length === 0) {
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text('No incidents recorded for this asset.', 14, y + 4);
  } else {
    autoTable(doc, {
      startY: y,
      head: [['Date', 'Driver', 'Location', 'G-force', 'Status']],
      body: incidents.map(i => [
        formatDateTime(i.detectedAt),
        i.driverName || '—',
        i.location || '—',
        i.gforce ? `${i.gforce}G` : '—',
        (i.status || '—').toUpperCase(),
      ]),
      styles: { fontSize: 7.5, cellPadding: 2 },
      headStyles: { fillColor: [br, bg, bb], textColor: 255, fontStyle: 'bold', fontSize: 7 },
      margin: { left: 14, right: 14 },
    });
  }

  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    addFooter(doc, org, i, pageCount);
  }
  doc.save(`asset-history-${asset.reg || asset.id || 'export'}.pdf`);
};

// ─── 6. Audit Log PDF ─────────────────────────────────────────────────────────

export const generateAuditLogPDF = (entries, org, dateRange) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const brandColour = org?.brandColour || '#1A3A5C';
  const [br, bg, bb] = hexToRgb(brandColour);

  const label = dateRange || 'Full export';

  addHeader(doc, { ...org, subtitle: 'All branches · Full audit export' }, 'AUDIT LOG', label, brandColour);
  addStatusBar(doc, `${entries.length} ENTRIES · ${label}`, `Super admin export · Generated ${formatDateTime(new Date())}`, 'info');

  const AUDIT_LABELS = {
    'asset.created': 'Asset added',
    'asset.blocked': 'Asset blocked',
    'asset.unblocked': 'Asset unblocked',
    'asset.deleted': 'Asset removed',
    'check.submitted': 'Check submitted',
    'check.failed': 'Check failed',
    'check.passed': 'Check passed',
    'incident.created': 'Incident created',
    'incident.closed': 'Incident closed',
    'user.invited': 'User invited',
    'user.activated': 'User activated',
    'user.removed': 'User removed',
    'user.profile_updated': 'Profile updated',
    'user.role_changed': 'Role changed',
    'template.created': 'Template created',
    'template.updated': 'Template updated',
    'template.deleted': 'Template deleted',
    'branch.created': 'Branch added',
    'org.settings_updated': 'Settings updated',
    'staff.invited': 'Staff invited',
    'staff.removed': 'Staff removed',
    'derv.tank_created': 'Tank added',
    'derv.order_placed': 'Derv order placed',
    'auth.login': 'Signed in',
    'auth.logout': 'Signed out',
  };

  const ACTION_COLOURS = {
    'asset.blocked': [220, 53, 69],
    'asset.unblocked': [40, 167, 69],
    'asset.deleted': [220, 53, 69],
    'check.failed': [220, 53, 69],
    'check.passed': [40, 167, 69],
    'incident.created': [220, 53, 69],
    'user.invited': [26, 58, 92],
    'user.removed': [220, 53, 69],
    'auth.login': [40, 167, 69],
  };

  autoTable(doc, {
    startY: 52,
    head: [['Date & time', 'Action', 'Performed by', 'Target', 'Details']],
    body: entries.map(e => [
      formatDateTime(e.createdAt),
      AUDIT_LABELS[e.action] || e.action,
      e.performedByName || '—',
      e.targetName || e.targetId || '—',
      e.details || '—',
    ]),
    styles: { fontSize: 7.5, cellPadding: 2, overflow: 'ellipsize' },
    headStyles: { fillColor: [br, bg, bb], textColor: 255, fontStyle: 'bold', fontSize: 7 },
    columnStyles: {
      0: { cellWidth: 32 },
      1: { cellWidth: 28, fontStyle: 'bold' },
      2: { cellWidth: 30 },
      3: { cellWidth: 30 },
      4: { cellWidth: 'auto' },
    },
    didDrawCell: (data) => {
      if (data.section === 'body' && data.column.index === 1) {
        const action = entries[data.row.index]?.action;
        const colour = ACTION_COLOURS[action];
        if (colour) doc.setTextColor(...colour);
      }
    },
    margin: { left: 14, right: 14 },
  });

  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    addFooter(doc, org, i, pageCount);
  }
  doc.save(`audit-log-${label.replace(/\s/g, '-').toLowerCase()}.pdf`);
};